import React from 'react';
import { Box, Typography, Button } from '@mui/material';
import { useHistory } from 'react-router-dom';
import PlaylistAddCheckOutlinedIcon from '@material-ui/icons/PlaylistAddCheckOutlined';
import ThumbUpOutlinedIcon from '@material-ui/icons/ThumbUpOutlined';

/**
 * Solutions > COS Readiness Solution > Overview — Common Section screen,
 * shared by the Partner, School and Teacher workspaces (both
 * /dq_pro/solutions/readiness and /dq_pro/solutions/readiness/overview show
 * this same content, matching how /dq_pro/solutions and
 * /dq_pro/solutions/overview both render SolutionsOverviewPage). Kept
 * alongside that page in the generic Solutions/ folder for the same reason —
 * it belongs to all three workspaces, not one persona.
 *
 * Layout/copy came from the reference mockup (CS-Solution-P1-overview);
 * colors/shapes follow this app's own tokens, same as SolutionsOverviewPage.
 * Two things from the mockup were annotated out and are NOT built here:
 * the floating "COS Readiness Solution / Working on day one" side card, and
 * the whole "Add when a school needs it" (Define/Certify/Benchmark) section.
 */

const BORDER = 'var(--brand-border, #E5E7EB)';
const MUTED = 'var(--brand-text-muted, #6B7280)';
const TEXT = 'var(--brand-text, #111827)';
const ORANGE = '#FF6900';

const DAY_ONE_STEPS = [
    {
        number: '1',
        category: 'Measure',
        title: 'COS Children Assessment',
        desc: 'A baseline showing where each student stands, feeding dashboards by student, class, and school — so teachers know where to focus before teaching anything.',
        overviewPath: '/dq_pro/solutions/children-assessment/overview',
        previewPath: '/dq_pro/solutions/children-assessment/preview',
    },
    {
        number: '2',
        category: 'Build',
        title: 'LearnCreator',
        desc: 'Teachers set what a lesson should cover; LearnCreator writes it — age-appropriate, curriculum-aligned, taught standalone or inside existing subjects.',
        overviewPath: '/dq_pro/solutions/learncreator/overview',
        previewPath: '/dq_pro/solutions/learncreator/preview',
    },
    {
        number: '3',
        category: 'Track',
        title: 'Progress dashboards',
        desc: 'Checks built into every lesson track growth over time, so schools can report progress to families, boards, and authorities.',
    },
];

const WHY_SCHOOLS_CHOOSE_IT = [
    {
        title: 'A system, not an assembly',
        desc: "Builds capability across the year instead of a single session that fades by term's end.",
    },
    {
        title: 'Fits the timetable you have',
        desc: 'Run it as its own course, or weave it into subjects the school already teaches.',
    },
    {
        title: 'Measured per student',
        desc: 'A baseline plus embedded checks evidences real growth, not attendance.',
    },
    {
        title: 'Child protection at the core',
        desc: 'Built by a standards body specialized in child online safety, not a general e-learning vendor.',
    },
];

const primaryButtonSx = {
    backgroundColor: ORANGE,
    color: '#fff',
    textTransform: 'none',
    fontWeight: 700,
    fontSize: '14px',
    borderRadius: '50px',
    px: 2.5,
    py: 0.9,
    whiteSpace: 'nowrap',
    '&:hover': { backgroundColor: '#e55e00' },
};

const outlineButtonSx = {
    borderColor: ORANGE,
    color: ORANGE,
    textTransform: 'none',
    fontWeight: 700,
    fontSize: '14px',
    borderRadius: '50px',
    px: 2.5,
    py: 0.9,
    whiteSpace: 'nowrap',
    '&:hover': { borderColor: '#e55e00', backgroundColor: 'rgba(255, 105, 0, 0.06)' },
};

const plainCardSx = {
    border: `1px solid ${BORDER}`,
    borderRadius: '14px',
    boxShadow: '0px 2px 8px rgba(0,0,0,0.04)',
    backgroundColor: 'var(--brand-surface, #ffffff)',
};

const sectionLabelSx = {
    fontSize: '11px', fontWeight: 700, letterSpacing: '0.6px',
    textTransform: 'uppercase', color: MUTED,
};

const cardHeaderIconSx = {
    width: 36, height: 36, borderRadius: '10px',
    backgroundColor: 'rgba(255, 105, 0, 0.08)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    flexShrink: 0,
};

const CardHeader = ({ icon: Icon, title, badge }) => (
    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1.5, pb: 2, mb: 2.5, borderBottom: `1px solid ${BORDER}` }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Box sx={cardHeaderIconSx}>
                <Icon sx={{ fontSize: 20, color: ORANGE }} />
            </Box>
            <Typography sx={{ fontSize: '17px', fontWeight: 800, color: TEXT }}>
                {title}
            </Typography>
        </Box>
        {badge && <Typography sx={sectionLabelSx}>{badge}</Typography>}
    </Box>
);

const SolutionReadinessOverviewPage = () => {
    const history = useHistory();

    return (
        <Box sx={{ width: '100%', maxWidth: '100%', px: { xs: 2, sm: 0 }, py: { xs: 3, sm: 5 } }}>
            <Typography sx={{ fontSize: { xs: '24px', sm: '30px' }, fontWeight: 800, color: TEXT, mb: 1.5, }}>
                Build students who are safe and capable online
            </Typography>
            <Typography sx={{ fontSize: '15px', color: MUTED, lineHeight: 1.6, mb: 4, }}>
                One connected system that measures where every student stands, builds the lessons to close the gap,
                and tracks their growth — anchored in your national curriculum and global child protection standards.
            </Typography>

            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)' }, gap: 3, mb: 4, alignItems: 'stretch' }}>
                {/* Card 1 — Working on day one */}
                <Box sx={{ ...plainCardSx, p: { xs: 3, sm: 3.5 }, display: 'flex', flexDirection: 'column' }}>
                    <CardHeader icon={PlaylistAddCheckOutlinedIcon} title="Working on day one" badge="Included" />
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                        {DAY_ONE_STEPS.map((step) => (
                            <Box key={step.number} sx={{ display: 'flex', flexDirection: 'column' }}>
                                <Typography sx={{ fontSize: '11px', fontWeight: 700, letterSpacing: '0.4px', textTransform: 'uppercase', color: MUTED, mb: 0.75 }}>
                                    {`${step.number} · ${step.category}`}
                                </Typography>
                                <Typography sx={{ fontSize: '14.5px', fontWeight: 700, color: TEXT, mb: 0.5 }}>
                                    {step.title}
                                </Typography>
                                <Typography sx={{ fontSize: '13.5px', color: MUTED, lineHeight: 1.7, mb: step.overviewPath ? 1 : 0 }}>
                                    {step.desc}
                                </Typography>
                                {step.overviewPath && (
                                    <Box sx={{ display: 'flex', gap: 1.25, alignSelf: 'flex-end' }}>
                                        <Button sx={primaryButtonSx} onClick={() => history.push(step.overviewPath)}>
                                            Overview
                                        </Button>
                                        {step.previewPath && (
                                            <Button variant="outlined" sx={outlineButtonSx} onClick={() => history.push(step.previewPath)}>
                                                Preview
                                            </Button>
                                        )}
                                    </Box>
                                )}
                            </Box>
                        ))}
                    </Box>
                </Box>

                {/* Card 2 — Why schools choose it */}
                <Box sx={{ ...plainCardSx, p: { xs: 3, sm: 3.5 }, display: 'flex', flexDirection: 'column' }}>
                    <CardHeader icon={ThumbUpOutlinedIcon} title="Why schools choose it" />
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
                        {WHY_SCHOOLS_CHOOSE_IT.map((item) => (
                            <Box key={item.title}>
                                <Typography sx={{ fontSize: '14.5px', fontWeight: 700, color: TEXT, mb: 0.5 }}>
                                    {item.title}
                                </Typography>
                                <Typography sx={{ fontSize: '13.5px', color: MUTED, lineHeight: 1.7 }}>
                                    {item.desc}
                                </Typography>
                            </Box>
                        ))}
                    </Box>
                </Box>
            </Box>

            {/* Anchored in */}
            <Box sx={{ ...plainCardSx, p: { xs: 3, sm: 3.5 } }}>
                <Typography sx={{ ...sectionLabelSx, mb: 1 }}>Anchored in</Typography>
                <Typography sx={{ fontSize: '13.5px', color: MUTED, lineHeight: 1.7 }}>
                    IEEE 3527.1 · UNESCO · OECD · your national curriculum · 100+ global frameworks · the CPC Index,
                    the global benchmark for child online protection
                </Typography>
            </Box>
        </Box>
    );
};

export default SolutionReadinessOverviewPage;
